import { useState, useEffect, useRef } from 'react';
import { portfolioItems } from '../../../mocks/portfolio';

export default function Portfolio() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [visibleItems, setVisibleItems] = useState<Set<number>>(new Set());
  const gridRef = useRef<HTMLDivElement>(null);

  const filters = ['All', 'Kitchen', 'Bathroom', 'Painting', 'Flooring', 'Drywall'];

  const filteredItems = activeFilter === 'All'
    ? portfolioItems
    : portfolioItems.filter(item => item.category === activeFilter);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = Number(entry.target.getAttribute('data-id'));
            if (id) {
              setVisibleItems(prev => new Set(prev).add(id));
            }
          }
        });
      },
      { threshold: 0.15 }
    );

    const cards = gridRef.current?.querySelectorAll('[data-id]');
    cards?.forEach(card => observer.observe(card));

    return () => observer.disconnect();
  }, [filteredItems]);

  useEffect(() => {
    setVisibleItems(new Set());
  }, [activeFilter]);

  return (
    <section id="portfolio" className="py-20 md:py-28 lg:py-32 bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10 md:mb-14">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 bg-[#E8F0E8] text-[#3D6B4F] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              <i className="ri-instagram-line text-sm"></i>
              <span>Our Instagram Portfolio</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4 font-serif leading-tight">
              See Our Work<br />Come to Life
            </h2>
            <p className="text-sm md:text-base text-gray-500 leading-relaxed">
              Follow our journey on Instagram and explore our latest transformations, design inspiration, and real client results from Winter Garden, FL.
            </p>
          </div>
          <a
            href="https://www.instagram.com/bestcallrestor/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2.5 bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] text-white px-6 py-3 rounded-lg text-sm font-semibold hover:shadow-lg hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer self-start lg:self-end"
          >
            <i className="ri-instagram-line text-lg"></i>
            @bestcallrestor
          </a>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2 sm:gap-2.5 mb-10 md:mb-12">
          {filters.map((filter) => (
            <button
              key={filter}
              onClick={() => setActiveFilter(filter)}
              className={`px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                activeFilter === filter
                  ? 'bg-gray-900 text-white shadow-md'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-400 hover:text-gray-900'
              }`}
            >
              {filter}
            </button>
          ))}
        </div>

        {/* Instagram-Style Photo Grid */}
        <div
          ref={gridRef}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 mb-10"
        >
          {filteredItems.map((item, index) => (
            <div
              key={item.id}
              data-id={item.id}
              className={`relative group cursor-pointer overflow-hidden rounded-xl transition-all duration-700 ${
                visibleItems.has(item.id)
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${index * 60}ms` }}
              onMouseEnter={() => setHoveredId(item.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div className="w-full aspect-square">
                <img
                  src={item.image}
                  alt={`${item.title} - Best Call Restoration remodeling project in Winter Garden FL`}
                  title={`${item.title} - ${item.category} Remodeling`}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                  loading="lazy"
                />
              </div>

              {/* Hover Overlay */}
              <div
                className={`absolute inset-0 bg-black/50 flex flex-col items-center justify-center transition-opacity duration-300 ${
                  hoveredId === item.id ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <div className="flex items-center gap-6 text-white mb-3">
                  <div className="flex items-center gap-1.5">
                    <i className="ri-heart-fill text-lg"></i>
                    <span className="text-sm font-bold">{item.likes}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <i className="ri-chat-3-fill text-lg"></i>
                    <span className="text-sm font-bold">{Math.floor(item.likes * 0.15)}</span>
                  </div>
                </div>
                <p className="text-white text-xs font-semibold uppercase tracking-wider opacity-80">
                  {item.category}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="bg-[#E8F0E8] rounded-2xl p-8 sm:p-10 md:p-12 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 font-serif">
              Love what you see?
            </h3>
            <p className="text-sm text-gray-600">
              Follow us for daily updates, behind-the-scenes, and more transformations.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <a
              href="https://www.instagram.com/bestcallrestor/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-gray-800 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-instagram-line text-base"></i>
              Follow on Instagram
            </a>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-white text-gray-900 px-6 py-3 rounded-lg text-sm font-semibold border border-gray-200 hover:border-gray-400 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Get Free Estimate
              <i className="ri-arrow-right-line text-base"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}